/**
 * La lista de espera del negocio (spec 029).
 *
 * Solo lectura. Por el día que esperan, con el puesto de ese día.
 * Vacío y «no se pudo leer» se dicen aparte.
 */
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { api } from '../../lib/api'
import { agruparPorDia, describirFranja } from '../../lib/espera'
import { useFichaNegocio } from '../../lib/ficha-negocio'
import { fechaHora } from '../../lib/tiempo'
import { useIncluirTestsVista } from '../../lib/use-incluir-tests'
import { useEsAdminUi } from '../../ver-como'

export interface Asiento {
    conversationId: number | null
    contactId: number | null
    servicioId: number
    profesionalId: number | null
    fecha: string | null
    horaMinima: string | null
    horaMaxima: string | null
    correo: string | null
    apuntadoEn: string
}

interface Respuesta {
    accountId: number
    ok: boolean
    motivo: string | null
    asientos: Asiento[]
}

interface Catalogo {
    profesionales: { id: number; nombre: string }[]
    servicios: { id: number; nombre: string }[]
}

export { describirDeseo } from '../../lib/espera'

export function EsperaNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    const esAdmin = useEsAdminUi()
    const { esDemo } = useFichaNegocio(accountId)
    const { incluirTests } = useIncluirTestsVista(accountId, esDemo, esAdmin)
    const periodoHref = `/business/${accountId}/counter/enEspera?periodo=30d${incluirTests ? '&incluirTests=1' : ''}`

    const { data, isPending, isError } = useQuery<Respuesta>({
        queryKey: ['espera', accountId],
        queryFn: () => api.get<Respuesta>(`/business/${accountId}/espera`),
        enabled: Boolean(accountId),
    })

    const { data: catalogo } = useQuery<Catalogo>({
        queryKey: ['espera-catalogo', accountId],
        queryFn: () => api.get<Catalogo>(`/business/${accountId}/calendar`),
        retry: false,
        staleTime: 5 * 60_000,
    })

    const grupos = data?.ok ? agruparPorDia(data.asientos) : []

    return (
        <div className="space-y-5 font-sans">
            <header>
                <h2 className="text-lg font-semibold text-neutral-100">Lista de espera</h2>
                <p className="mt-1 text-sm text-neutral-400">
                    Quien pulsó Apúntame, por el día que espera. El puesto es el de ese día:
                    si se libera un hueco, se ofrece al 1. Desde aquí no se quita a nadie.
                </p>
                {data?.ok && (
                    <p className="mt-2 text-sm tabular-nums text-neutral-200">
                        {data.asientos.length === 0
                            ? 'Nadie en la cola ahora.'
                            : data.asientos.length === 1
                              ? '1 persona en la cola ahora.'
                              : `${data.asientos.length} personas en la cola ahora.`}
                    </p>
                )}
            </header>

            {accountId && (
                <p className="text-sm text-neutral-400">
                    <Link
                        to={periodoHref}
                        className="text-sky-400 hover:text-sky-300"
                    >
                        Conversaciones de los últimos 30 días que acabaron en la lista
                    </Link>
                </p>
            )}

            {isPending && <p className="text-sm text-neutral-500">Cargando…</p>}
            {isError && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                    No se pudo leer la lista de espera.
                </p>
            )}

            {data && !data.ok && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                    No se pudo leer la lista de espera
                    {data.motivo ? `: ${data.motivo}` : '.'}
                </p>
            )}

            {data?.ok && data.asientos.length === 0 && (
                <p className="text-sm text-neutral-500">Nadie en espera.</p>
            )}

            {grupos.map((g) => (
                <section key={g.clave} className="space-y-2">
                    <h3 className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-neutral-100">
                        <span>{g.titulo}</span>
                        <span className="font-normal tabular-nums text-neutral-500">
                            {g.asientos.length === 1 ? '1 persona' : `${g.asientos.length} personas`}
                        </span>
                    </h3>
                    <ul className="divide-y divide-neutral-800 overflow-hidden rounded-xl border border-neutral-800">
                        {g.asientos.map((a, i) => {
                            const serv =
                                catalogo?.servicios.find((s) => s.id === a.servicioId)?.nombre ??
                                `servicio ${a.servicioId}`
                            const prof =
                                a.profesionalId === null
                                    ? null
                                    : (catalogo?.profesionales.find((p) => p.id === a.profesionalId)
                                          ?.nombre ?? `profesional ${a.profesionalId}`)
                            const clave = a.conversationId ?? `${g.clave}-${i}`
                            return (
                                <li
                                    key={clave}
                                    className="flex flex-col gap-1 px-4 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                                >
                                    <div className="flex min-w-0 gap-3">
                                        <p className="shrink-0 text-sm font-semibold tabular-nums text-orange-300">
                                            {a.puesto}º
                                        </p>
                                        <div className="min-w-0">
                                            <p className="text-sm text-neutral-100">
                                                {esAdmin && a.conversationId !== null ? (
                                                    <Link
                                                        to={`/business/${accountId}/console/${a.conversationId}`}
                                                        className="text-sky-400 hover:text-sky-300"
                                                    >
                                                        Conversación #{a.conversationId}
                                                    </Link>
                                                ) : a.conversationId !== null ? (
                                                    <>Conversación #{a.conversationId}</>
                                                ) : (
                                                    <>Alguien en espera</>
                                                )}
                                                <span className="text-neutral-500"> · {serv}</span>
                                                {prof && (
                                                    <span className="text-neutral-500"> · {prof}</span>
                                                )}
                                            </p>
                                            <p className="mt-0.5 text-sm text-neutral-400">
                                                {describirFranja(a)}
                                            </p>
                                            {a.correo && (
                                                <p className="mt-0.5 text-xs text-neutral-500">
                                                    {a.correo}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <p className="shrink-0 pl-8 text-sm text-neutral-500 sm:pl-0">
                                        Apuntado {fechaHora(a.apuntadoEn)}
                                    </p>
                                </li>
                            )
                        })}
                    </ul>
                </section>
            ))}
        </div>
    )
}
